/** Student registration form section. */
import * as React from 'react';
import { User, Lock, Mail } from 'lucide-react';
import { Input } from '@/components/forms/Input';
import { RadioGroup } from '@/components/forms/RadioGroup';
import { Select } from '@/components/forms/Select';
import { SmartContactInput } from '@/components/forms/SmartContactInput';
import type { StoredContact } from '@/components/forms/SmartContactInput';
import { PhoneInput } from '@/components/forms/PhoneInput';
import { DateSplitInput } from '@/components/forms/DateSplitInput';
import { Textarea } from '@/components/forms/Textarea';
import { AddressAutocompleteInput } from '@/components/forms/AddressAutocompleteInput';
import { Autocomplete } from '@/components/forms/Autocomplete';
import { DepartmentSelect } from '@/components/common/DepartmentSelect';
import { useActorEstablishmentAssignmentScope } from '@/hooks/useActorEstablishmentAssignmentScope';
import { useEnrichEstablishmentLabels } from '@/hooks/useEnrichEstablishmentLabels';
import {
  appendOtherEstablishmentOption,
  schoolEstablishmentRefFromId,
} from '@/utils/other-establishment-utils';
import {
  appendSelectedEstablishmentOption,
  firstEstablishmentId,
  mergeEstablishmentLabelCache,
  shouldApplySingleEstablishmentLock,
  type EstablishmentLabelEntry,
} from '@/components/students/inperson/student-form-utils';
import {
  STUDENT_GENDER_OPTIONS_WITHOUT_OTHER as CHILD_GENDER_OPTIONS,
  SCHOOL_SITUATION_OPTIONS,
  SCHOOL_CURRICULUM_OPTIONS,
  BOOLEAN_SELECT_OPTIONS,
  HIGH_SCHOOL_CURRICULUM_OPTIONS,
  STUDENT_ACCOUNT_STATUS_CONFIG,
} from '@/components/students/student-configs';
import {
  applyRemoteStudentPrimaryContactChange,
  extractDepartmentCodeFromLabel,
  isStudentAddressLockedToParent,
  linkedParentUserId,
  resolveAdultContactError,
  resolveAdultContactState,
  studentContactHelperText,
} from '@/components/students/remote/student-form-utils';
import {
  writeUnifiedContactSwap,
  canSwapPrimaryContact,
  primaryContactFieldError,
} from '@/utils/user-contact-utils';
import { Button } from '@/components/forms/Button';
import type { ContactKind } from '@/types/user.types';
import type { useFormValidation } from '@/hooks/useFormValidation';

/** Validation API passed into the registration section. */
type FormValidation = ReturnType<typeof useFormValidation>;

/** Establishment {@link Autocomplete} scoped to the actor's assigned establishments. */
interface StudentEstablishmentFieldProps {
  establishmentIds: string[];
  onChange: (values: string[]) => void;
  onBlur: () => void;
  state?: 'default' | 'error' | 'success';
  error?: string;
  inputSize?: 'sm' | 'md';
  departmentFilter?: string;
}

/**
 * Local helper: establishment field with scope-aware filtering and single-lock.
 * NOT exported — remains internal to this section.
 */
function StudentEstablishmentField({
  establishmentIds,
  onChange,
  onBlur,
  state,
  error,
  inputSize = 'md',
  departmentFilter,
}: Readonly<StudentEstablishmentFieldProps>) {
  const {
    selectOptions,
    isSingleLocked,
    singleEstablishment,
    singleEstablishmentLabel,
    isLoading: establishmentScopeLoading,
    isSearchLoading,
    onSearch,
    helperText,
  } = useActorEstablishmentAssignmentScope({
    departmentCode: departmentFilter,
    validatedProspectionOnly: false,
    requireDepartment: true,
  });

  const [labelCache, setLabelCache] = React.useState<Map<string, string>>(new Map());
  const primeLabelCache = React.useCallback((entries: EstablishmentLabelEntry[]) => {
    setLabelCache((prev) => mergeEstablishmentLabelCache(prev, entries));
  }, []);

  const selectedId = firstEstablishmentId(establishmentIds);
  useEnrichEstablishmentLabels(establishmentIds, primeLabelCache);

  const displayOptions = React.useMemo(
    () =>
      appendOtherEstablishmentOption(
        appendSelectedEstablishmentOption(selectOptions, selectedId, labelCache),
      ),
    [selectOptions, selectedId, labelCache],
  );

  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;

  React.useEffect(() => {
    if (
      !shouldApplySingleEstablishmentLock(
        isSingleLocked,
        singleEstablishment?.id,
        establishmentIds,
      ) ||
      !singleEstablishment
    ) {
      return;
    }
    onChangeRef.current([singleEstablishment.id]);
  }, [isSingleLocked, singleEstablishment, establishmentIds]);

  function handleEstablishmentChange(val: string): void {
    onChange(val ? [val] : []);
  }

  if (isSingleLocked && singleEstablishmentLabel) {
    return (
      <Input
        label="Établissement"
        value={singleEstablishmentLabel}
        disabled
        required
        helperText="Établissement assigné à votre compte"
        state={state}
        error={error}
        inputSize={inputSize}
      />
    );
  }

  return (
    <Autocomplete
      label="Établissement"
      placeholder="Rechercher parmi vos établissements..."
      options={displayOptions}
      value={selectedId}
      onChange={handleEstablishmentChange}
      onBlur={onBlur}
      onSearch={onSearch}
      isLoading={establishmentScopeLoading || isSearchLoading}
      helperText={departmentFilter ? helperText : undefined}
      required
      state={state}
      error={error}
      inputSize={inputSize}
      minSearchLength={departmentFilter ? 0 : 2}
    />
  );
}

/** Props for {@link StudentRegistrationSection}. */
type StudentRegistrationSectionProps = Readonly<{
  formData: Record<string, any>;
  onChange: (key: string, value: any) => void;
  onBlur?: (field: string) => void;
  validation?: FormValidation;
  levelOptions: Array<{ value: string; label: string }>;
  levels: any[];
  contactType: ContactKind;
  setContactType: (type: ContactKind) => void;
  secondaryContact: string;
  setSecondaryContact: (value: string) => void;
  isAdult: boolean;
  parentOptions?: Array<{ value: string; label: string }>;
  isParentFetching?: boolean;
  onParentSearch?: (query: string) => void;
  mode: 'create' | 'edit';
  initialParentUserId?: string;
  initialParentName?: string;
  defaultCountry: string;
  /** Edit mode only */
  userId?: string;
  emailVerified?: boolean;
  /** Edit mode: primary contact exists check result (adult only) */
  primaryContactExists?: boolean | null;
  /** Edit mode: primary and secondary contact have the same value */
  contactsAreIdentical?: boolean;
  /** Edit: server email for dirty check */
  serverEmail?: string | null;
  /** Edit: server phone for dirty check */
  serverPhone?: string | null;
  /** Edit: server primary kind for dirty check */
  serverPrimaryKind?: 'email' | 'phone' | null;
}>;

/** Student registration form section. */
export function StudentRegistrationSection({
  formData,
  onChange,
  onBlur,
  validation,
  levelOptions,
  levels,
  contactType,
  setContactType,
  secondaryContact,
  setSecondaryContact,
  isAdult,
  parentOptions = [],
  isParentFetching = false,
  onParentSearch,
  mode,
  initialParentUserId,
  initialParentName,
  defaultCountry,
  userId,
  emailVerified,
  primaryContactExists,
  contactsAreIdentical,
  serverEmail,
  serverPhone,
  serverPrimaryKind,
}: StudentRegistrationSectionProps) {
  const contactExistsError = isAdult
    ? primaryContactFieldError(primaryContactExists, formData.contactType, !!contactsAreIdentical)
    : undefined;
  const contactFieldState = resolveAdultContactState(
    isAdult,
    contactExistsError,
    validation?.getFieldState('contact'),
  );
  const contactFieldError = resolveAdultContactError(
    isAdult,
    contactExistsError,
    validation?.getFieldError('contact'),
  );
  const secondaryFieldError = isAdult ? validation?.getFieldError('secondaryContact') : undefined;

  const linkedParentId =
    linkedParentUserId(formData.parentUserId) || linkedParentUserId(initialParentUserId);
  const showLivesWithParentField = linkedParentId.length > 0;
  const addressLockedToParent = showLivesWithParentField
    ? isStudentAddressLockedToParent(linkedParentId, formData.livingWithParents)
    : false;

  /** Persist primary contact; reset secondary when the inferred type flips. */
  const handleContactChange = (value: string, type: ContactKind, phoneCountryCode?: string) => {
    const { patch, resetSecondaryContact } = applyRemoteStudentPrimaryContactChange(
      formData.contactType,
      value,
      type,
      phoneCountryCode,
    );
    for (const [key, nextValue] of Object.entries(patch)) {
      onChange(key, nextValue);
    }
    setContactType(type);
    // Reset secondary contact if type changes
    if (resetSecondaryContact) setSecondaryContact('');
  };

  /** Adult edit: primary + swap + secondary; otherwise locked/create layout. */
  function renderContactBlock(): React.ReactNode {
    if (isAdult && mode === 'edit') {
      return (
        <div className="sm:col-span-3 grid grid-cols-2 items-start gap-2">
          <SmartContactInput
            label="Contact propre (optionnel)"
            value={formData.contact ?? formData.email ?? ''}
            onContactChange={handleContactChange}
            onBlur={() => onBlur?.('contact')}
            storedContact={liveStoredContact}
            state={contactFieldState}
            error={contactFieldError}
            helperText={studentContactHelperText(mode, isAdult)}
            inputSize="sm"
            showContactActions
            emailVerified={emailVerified}
            userId={userId}
            swapButton={swapButton}
          />
          {renderSecondaryContact()}
        </div>
      );
    }
    return (
      <>
        <SmartContactInput
          label={isAdult ? 'Contact propre (optionnel)' : 'Contact (hérité du parent)'}
          value={formData.contact ?? formData.email ?? ''}
          onContactChange={handleContactChange}
          onBlur={() => onBlur?.('contact')}
          required={!isAdult}
          disabled={!isAdult}
          storedContact={mode === 'edit' ? liveStoredContact : undefined}
          state={contactFieldState}
          error={contactFieldError}
          helperText={studentContactHelperText(mode, isAdult)}
          inputSize="sm"
          showContactActions={mode === 'edit'}
          emailVerified={emailVerified}
          userId={userId}
          usesParentCredentials={!isAdult}
        />
        {renderSecondaryContact()}
      </>
    );
  }

  /** Swap primary and secondary, then sync local contact state. */
  function handleSwapContacts(): void {
    const swapped = writeUnifiedContactSwap(
      onChange,
      {
        contact: formData.contact,
        contactType: formData.contactType,
        contactCountryCode: formData.contactCountryCode,
        secondaryContact,
        secondaryContactCountryCode: formData.secondaryContactCountryCode,
      },
      defaultCountry,
    );
    setSecondaryContact(swapped.secondaryContact ?? '');
    setContactType(swapped.contactType as ContactKind);
  }

  /** Swap beside the primary field only while the primary is a real email or phone. */
  const swapButton = canSwapPrimaryContact(formData.contactType) ? (
    <Button type="button" preset="contact-swap" size="md" onClick={handleSwapContacts} />
  ) : undefined;

  /** storedContact with live primaryKind so the field mode syncs after a swap. */
  const liveStoredContact: StoredContact = {
    primaryKind: formData.contactType as 'email' | 'phone',
    serverPrimaryKind,
    email: serverEmail,
    phone: serverPhone,
  };

  const showSecondaryContact = contactType !== 'unknown' && (isAdult || !!secondaryContact.trim());
  const secondaryDisabled = !isAdult;

  /** Secondary phone or email, opposite of the primary type. */
  function renderSecondaryContact(): React.ReactNode {
    if (!showSecondaryContact) return null;
    if (contactType === 'email') {
      return (
        <PhoneInput
          label="Téléphone (contact secondaire, optionnel)"
          value={secondaryContact}
          onChange={handleSecondaryContactChange}
          onBlur={handleSecondaryBlur}
          onCountryChange={handleSecondaryCountryChange}
          helperText={
            isAdult ? 'Numéro secondaire pour joindre cet élève' : 'Téléphone du parent lié'
          }
          countryCode={formData.secondaryContactCountryCode ?? defaultCountry}
          inputSize="sm"
          disabled={secondaryDisabled}
          state={secondaryFieldError ? 'error' : 'default'}
          error={secondaryFieldError}
        />
      );
    }
    return (
      <Input
        type="text"
        label="Email (contact secondaire, optionnel)"
        value={secondaryContact}
        onChange={handleSecondaryContactChange}
        onBlur={handleSecondaryBlur}
        icon={Mail}
        placeholder="Ex: contact@example.com"
        inputSize="sm"
        helperText={isAdult ? 'Email secondaire pour joindre cet élève' : 'Email du parent lié'}
        disabled={secondaryDisabled}
        state={secondaryFieldError ? 'error' : 'default'}
        error={secondaryFieldError}
      />
    );
  }

  /** Persist secondary phone digits or email. */
  function handleSecondaryContactChange(e: React.ChangeEvent<HTMLInputElement>): void {
    const value = e.target.value;
    setSecondaryContact(value);
    onChange('secondaryContact', value);
  }

  /** Mark secondary contact touched. */
  function handleSecondaryBlur(): void {
    onBlur?.('secondaryContact');
  }

  /** Persist secondary-phone country ISO. */
  function handleSecondaryCountryChange(country: { code: string }): void {
    onChange('secondaryContactCountryCode', country.code);
  }

  return (
    <div className="form-section">
      <h4 className="form-section-title">
        <User className="w-4 h-4" />
        Informations d'inscription
      </h4>
      <div className="grid grid-cols-1 gap-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Input
            label="Prénom"
            value={formData.firstName ?? ''}
            onChange={(e) => onChange('firstName', e.target.value)}
            onBlur={() => onBlur?.('firstName')}
            required
            state={validation?.getFieldState('firstName')}
            error={validation?.getFieldError('firstName')}
            inputSize="sm"
            placeholder="Ex: Marie"
          />
          <Input
            label="Nom"
            value={formData.lastName ?? ''}
            onChange={(e) => onChange('lastName', e.target.value)}
            onBlur={() => onBlur?.('lastName')}
            required
            state={validation?.getFieldState('lastName')}
            error={validation?.getFieldError('lastName')}
            inputSize="sm"
            placeholder="Ex: Martin"
          />
          <Select
            label="Genre"
            value={formData.gender ?? ''}
            onChange={(val) => onChange('gender', val)}
            onBlur={() => onBlur?.('gender')}
            options={CHILD_GENDER_OPTIONS}
            required
            state={validation?.getFieldState('gender')}
            error={validation?.getFieldError('gender')}
            size="sm"
            placeholder="Sélectionner"
          />
        </div>
        {formData.status === 'archived' ? (
          <Input
            label="Statut"
            value={STUDENT_ACCOUNT_STATUS_CONFIG.archived.label}
            disabled
            inputSize="sm"
          />
        ) : null}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:items-end">
          {mode === 'create' &&
            (initialParentUserId ? (
              <div className="relative">
                <Input
                  label="Parent lié"
                  value={initialParentName || 'Parent sélectionné'}
                  disabled
                  className="pr-10 bg-muted"
                  inputSize="sm"
                />
                <Lock className="pointer-events-none absolute right-3 bottom-2.5 w-4 h-4 text-muted-foreground" />
              </div>
            ) : (
              <Autocomplete
                label="Parent lié"
                value={formData.parentUserId ?? ''}
                onChange={(val) => onChange('parentUserId', val)}
                onSearch={onParentSearch}
                options={parentOptions}
                isLoading={isParentFetching}
                placeholder="Rechercher le parent de l'élève..."
                minSearchLength={2}
                inputSize="sm"
              />
            ))}
          <DateSplitInput
            label="Date de naissance"
            value={formData.dateOfBirth ?? ''}
            onChange={(value) => onChange('dateOfBirth', value)}
            onBlur={() => onBlur?.('dateOfBirth')}
            required
            state={validation?.getFieldState('dateOfBirth')}
            error={validation?.getFieldError('dateOfBirth')}
            maxDate={`${new Date().getFullYear() - 5}-12-31`}
            size="sm"
          />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {renderContactBlock()}
          <Input
            label="Code de connexion"
            value={formData.code ?? ''}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '').slice(0, 6);
              onChange('code', value);
            }}
            required
            helperText="6 chiffres exactement"
            inputSize="sm"
            placeholder="000000"
            maxLength={6}
            className="font-mono tracking-wider"
            state={validation?.getFieldState('code')}
            error={validation?.getFieldError('code')}
          />
        </div>
        {showLivesWithParentField && (
          <RadioGroup
            name="livingWithParents"
            label="L'enfant vit-il avec son parent ?"
            layout="horizontal"
            options={BOOLEAN_SELECT_OPTIONS}
            value={
              formData.livingWithParents == null || formData.livingWithParents === ''
                ? undefined
                : String(formData.livingWithParents)
            }
            onValueChange={(v) => onChange('livingWithParents', v === 'true')}
          />
        )}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <AddressAutocompleteInput
            label="Adresse"
            value={formData.address ?? ''}
            onChange={(value) => onChange('address', value)}
            disabled={addressLockedToParent}
            onSelection={(result) => {
              onChange('address', result.addressLine1);
              onChange('zipCode', result.postalCode);
              onChange('city', result.city);
              if (result.country) onChange('country', result.country);
              const deptCode = extractDepartmentCodeFromLabel(result.department);
              if (deptCode) {
                onChange('departmentId', deptCode);
                onChange('departmentName', '');
              }
            }}
            required
            size="sm"
          />
          <Input
            label="Code postal"
            value={formData.zipCode ?? ''}
            onChange={(e) => onChange('zipCode', e.target.value)}
            required
            inputSize="sm"
            placeholder="Ex: 75001"
            disabled={addressLockedToParent}
          />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Ville"
            value={formData.city ?? ''}
            onChange={(e) => onChange('city', e.target.value)}
            required
            inputSize="sm"
            placeholder="Ex: Paris"
            disabled={addressLockedToParent}
          />
          <Input
            label="Pays"
            value={formData.country ?? ''}
            onChange={(e) => onChange('country', e.target.value)}
            inputSize="sm"
            placeholder="Ex: France"
            disabled={addressLockedToParent}
          />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 items-end">
          <RadioGroup
            name="schoolSituation"
            label="Situation scolaire"
            required
            layout="horizontal"
            options={SCHOOL_SITUATION_OPTIONS}
            value={formData.schoolSituation ?? ''}
            onValueChange={(v) => onChange('schoolSituation', v)}
          />
          <Select
            label="Niveau"
            value={formData.levelId ?? ''}
            onChange={(val) => {
              onChange('levelId', val);
              const selectedLevel = levels.find((l: any) => l.id === val);
              onChange('level', { levelId: val, levelName: selectedLevel?.label ?? '' });
            }}
            options={levelOptions}
            required
            size="sm"
            placeholder="Sélectionner un niveau"
          />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <DepartmentSelect
            label="Département de l'établissement"
            value={formData.establishmentDepartmentCode ?? ''}
            onChange={(val) => {
              onChange('establishmentDepartmentCode', val);
              onChange('schoolEstablishment', []);
              onChange('schoolEstablishmentIds', []);
            }}
            placeholder="Filtrer par département"
            inputSize="sm"
          />
          <StudentEstablishmentField
            establishmentIds={formData.schoolEstablishmentIds ?? []}
            onChange={(values) => {
              onChange('schoolEstablishmentIds', values);
              onChange(
                'schoolEstablishment',
                values.map((id) => schoolEstablishmentRefFromId(id)),
              );
            }}
            onBlur={() => onBlur?.('schoolEstablishmentIds')}
            inputSize="sm"
            departmentFilter={formData.establishmentDepartmentCode || undefined}
          />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {formData.schoolSituation === 'high_school' && (
            <Select
              label="Type de lycée"
              value={formData.highSchoolType ?? ''}
              onChange={(val) => onChange('highSchoolType', val)}
              options={HIGH_SCHOOL_CURRICULUM_OPTIONS}
              size="sm"
              placeholder="Sélectionner"
            />
          )}
          <Select
            label="Cursus scolaire"
            value={formData.schoolCurriculum ?? ''}
            onChange={(val) => onChange('schoolCurriculum', val)}
            options={SCHOOL_CURRICULUM_OPTIONS}
            size="sm"
            placeholder="Sélectionner"
          />
        </div>
        <Textarea
          label="Besoins spécifiques"
          value={formData.specificNeeds ?? ''}
          onChange={(e) => onChange('specificNeeds', e.target.value)}
          rows={3}
          placeholder="Décrire les besoins spécifiques de l'élève"
        />
        <Textarea
          label="L'enfant a-t-il un statut juridique particulier ?"
          value={formData.legalStatus ?? ''}
          onChange={(e) => onChange('legalStatus', e.target.value)}
          rows={3}
          placeholder="Ex: ASE, réfugié, mineur non accompagné..."
        />
      </div>
    </div>
  );
}
